/** Shared treatment timing helpers for scheduling UI. */

export const parseTimeToMinutes = (value) => {
  if (value == null || value === '') return null;
  if (typeof value === 'number') return value;
  const normalized = String(value).trim().replace('.', ':');
  if (!normalized.includes(':')) {
    const parsed = parseInt(normalized, 10);
    return Number.isNaN(parsed) ? null : parsed;
  }
  const [hours, minutes] = normalized.split(':').map(Number);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;
  return hours * 60 + minutes;
};

export const formatMinutesToTime = (minutes) => {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

export const formatMinutesToDisplay = (minutes) => {
  const normalized = formatMinutesToTime(minutes);
  const [h, m] = normalized.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const hour12 = h % 12 || 12;
  return `${hour12}:${String(m).padStart(2, '0')} ${period}`;
};

export const getTreatmentFirstStartMinutes = (treatment) =>
  parseTimeToMinutes(treatment?.operational_hours?.first_start_time ?? treatment?.first_start_time)
  ?? 9 * 60;

export const getTreatmentLastEndMinutes = (treatment) =>
  parseTimeToMinutes(treatment?.operational_hours?.last_end_time ?? treatment?.last_end_time)
  ?? 17 * 60;

export const getTreatmentTotalMinutes = (treatment) =>
  (treatment?.duration || 60) + (treatment?.preparation_time || 0);

const parseAllowedStartTimes = (raw, firstStart, lastEnd) => {
  if (!raw || !String(raw).trim()) return [];
  const slots = [];
  for (const token of String(raw).split(',')) {
    const trimmed = token.trim();
    if (!trimmed) continue;
    const minutes = parseTimeToMinutes(trimmed);
    if (minutes == null) continue;
    let slot = minutes;
    if (firstStart != null && slot < firstStart && slot < 13 * 60) {
      slot += 12 * 60;
    }
    if (lastEnd != null && slot + 15 > lastEnd && slot < 13 * 60) {
      // keep afternoon shorthand handling aligned with scheduler
    }
    slots.push(slot);
  }
  return [...new Set(slots)].sort((a, b) => a - b);
};

/** Slots that fit the normal operational end time. */
export const getTreatmentSlotCandidates = (treatment) => {
  if (!treatment) return [];
  const firstStart = getTreatmentFirstStartMinutes(treatment);
  const lastEnd = getTreatmentLastEndMinutes(treatment);
  const totalTime = getTreatmentTotalMinutes(treatment);
  const allowed = parseAllowedStartTimes(treatment.allowed_start_times, firstStart, lastEnd);

  if (allowed.length > 0) {
    return allowed
      .filter((slot) => slot + totalTime <= lastEnd)
      .map(formatMinutesToTime);
  }

  const slots = [];
  for (let slot = firstStart; slot + totalTime <= lastEnd; slot += 15) {
    slots.push(formatMinutesToTime(slot));
  }
  return slots;
};

/**
 * All configured slots including those that run past operational end (up to 9 PM).
 * Each option: { value: 'HH:MM', afterHours: boolean }
 */
export const getTreatmentSlotOptions = (treatment) => {
  if (!treatment) return [];
  const firstStart = getTreatmentFirstStartMinutes(treatment);
  const lastEnd = getTreatmentLastEndMinutes(treatment);
  const totalTime = getTreatmentTotalMinutes(treatment);
  const dayCap = 21 * 60;
  const allowed = parseAllowedStartTimes(treatment.allowed_start_times, firstStart, lastEnd);

  if (allowed.length > 0) {
    return allowed
      .filter((slot) => slot + totalTime <= dayCap)
      .map((slot) => ({
        value: formatMinutesToTime(slot),
        afterHours: slot + totalTime > lastEnd,
      }));
  }

  const slots = [];
  for (let slot = firstStart; slot + totalTime <= dayCap; slot += 15) {
    slots.push({
      value: formatMinutesToTime(slot),
      afterHours: slot + totalTime > lastEnd,
    });
  }
  return slots;
};

export const isAfterOperationalEndStart = (treatment, startTime) => {
  const startMinutes = parseTimeToMinutes(startTime);
  if (startMinutes == null || !treatment) return false;
  return startMinutes + getTreatmentTotalMinutes(treatment) > getTreatmentLastEndMinutes(treatment);
};

export const buildAfterHoursConfirmMessage = (treatment, startTime) => {
  const startMinutes = parseTimeToMinutes(startTime);
  const lastEnd = getTreatmentLastEndMinutes(treatment);
  const endMinutes = startMinutes + getTreatmentTotalMinutes(treatment);
  const treatmentName = treatment?.name || 'this treatment';
  return (
    `${treatmentName} at ${formatMinutesToDisplay(startMinutes)} will run until ` +
    `${formatMinutesToDisplay(endMinutes)}, after the normal service end time ` +
    `(${formatMinutesToDisplay(lastEnd)}).\n\nBook this after-hours slot anyway?`
  );
};

export const bookingNeedsAfterHoursApproval = (treatment, preferredTime) => {
  if (!treatment || !preferredTime) return false;
  return isAfterOperationalEndStart(treatment, preferredTime);
};
